// QEW field access rules. Approved in STEP3_SIGNIN_APPROVED.md, 2026-09-29.
//
// No PIN. Field Access is the gate: bag tag or typed QEW ID, then one
// "Is this you?" name check per visit. The visit ends on Sign Out or after
// one hour with no activity. Unsent work stays on the device either way.
// Every record carries a stamp of who did it, where, on what device, and
// that the name was confirmed.

import { coversSite, type SignInClaims } from "./badge";

/** A visit ends after this many minutes with no activity. */
export const IDLE_LIMIT_MINUTES = 60;

/** True when the visit has gone idle for the full limit. */
export function isSessionExpired(lastActivity: Date, now: Date): boolean {
  const idleMs = now.getTime() - lastActivity.getTime();
  return idleMs >= IDLE_LIMIT_MINUTES * 60_000;
}

/** The fields every signed record carries. Names match the Connections schema where they overlap. */
export interface RecordStamp {
  "Recorded By": string;
  "Recorded By Name": string;
  "Site Number": string;
  "Recorded On": string;
  "Recorded At": string;
  "Name Confirmed": true;
}

export interface StampInput {
  siteNumber: string;
  /** Device name, as the app knows it. */
  device: string;
  /** Set true only after "Yes, that's me" this visit. */
  nameConfirmed: boolean;
  at: Date;
}

/** Builds the stamp. Refuses when the name check was skipped or the site is not on the badge. */
export function stampRecord(claims: SignInClaims, input: StampInput): RecordStamp {
  if (!input.nameConfirmed) {
    throw new Error("Confirm your name before saving.");
  }
  if (!coversSite(claims, input.siteNumber)) {
    throw new Error(`Your badge does not cover site ${input.siteNumber}.`);
  }
  return {
    "Recorded By": claims.badgeId,
    "Recorded By Name": claims.holderName,
    "Site Number": input.siteNumber,
    "Recorded On": input.device,
    "Recorded At": input.at.toISOString(),
    "Name Confirmed": true,
  };
}
